import { useCallback } from "react";
import { useTextToSpeech } from "@/hooks/use-text-to-speech";
import { useToast } from "@/hooks/use-toast";

interface UseSpeakTextOptions {
  rate?: number;
  lang?: string;
}

interface UseSpeakTextReturn {
  /** Toggle speaking the given text. Stops if already speaking/loading. */
  handleSpeak: (text: string, e?: React.MouseEvent) => void;
  isSpeaking: boolean;
  isLoading: boolean;
}

/**
 * Imperative text-to-speech helper for callers that can't render a
 * `SpeakButton` (e.g. the floating selection toolbar drives its own button).
 * Wraps `useTextToSpeech` with the shared browser-support recheck + toast.
 */
export const useSpeakText = (options: UseSpeakTextOptions = {}): UseSpeakTextReturn => {
  const { rate = 0.9, lang = "en-US" } = options;
  const { speak, stop, isSpeaking, isLoading, browserSupported, error, checkSupport } =
    useTextToSpeech({ rate, lang });
  const { toast } = useToast();

  const handleSpeak = useCallback(
    (text: string, e?: React.MouseEvent) => {
      e?.stopPropagation();

      if (!browserSupported) {
        checkSupport();
        if (!browserSupported) {
          toast({
            title: "Text-to-Speech Not Available",
            description: error || "Please use a modern browser like Chrome, Firefox, Safari, or Edge.",
            variant: "destructive",
          });
          return;
        }
      }

      if (isSpeaking || isLoading) {
        stop();
      } else {
        speak(text);
      }
    },
    [browserSupported, checkSupport, error, isSpeaking, isLoading, speak, stop, toast],
  );

  return { handleSpeak, isSpeaking, isLoading };
};
